import { el, mount, unmount, list } from 'redom';
import { format } from 'date-fns';

import application from 'application';
import serverInterface from 'server-interface';

import { findIndexByProperty } from 'utilities';

import externalEventCodes from 'event-system/codes/external';
import internalEventCodes from 'event-system/codes/internal';

import { formatToLongDate } from 'utilities/date';

import PollChoice from './poll-choice';
import EndedPollChoice from './ended-poll-choice';
import PollChoiceWithStats from './poll-choice-with-stats';

import {
  canDisplayEndedPollView,
  canDisplayActivePollView,
  canDisplayChoicesWithoutStats,
  filterEmptyPollChoices
} from './utilities';

class PollingBox extends HTMLElement {

  constructor() {

    super();

    this.attachShadow({ mode: 'open' });

    this.userId = null;
    this.postId = null;
    this.pollId = null;
    this.minuteEventRegistration = null;
    this.expiryLabel = el('span.poll-ended-view-expiry-label', {}, '');

    this.tryInitializeUserId();
    this.renderSpinner();

    mount(this.shadowRoot, this.getStyleEl());

  }

  connectedCallback() {

    const postIdAttributeName = 'data-postId';
    const pollIdAttributeName = 'data-pollId';

    const postId = this.getAttribute(postIdAttributeName);
    const pollId = this.getAttribute(pollIdAttributeName);

    this.loadPoll(postId, pollId);

  }

  disconnectedCallback() {

    this.unregisterEventListeners();

  }

  async loadPoll(postId, pollId) {

    try {

      this.poll = await serverInterface.startup.community.getPopulatedPollById(postId, pollId);

      filterEmptyPollChoices(this.poll);

      this.registerEventListeners(this.poll._id);
      this.tryRenderAll();

    } catch (exception) {

      this.removeSpinner();
      this.renderSomethingWentWrong();

    }

  }

  tryInitializeUserId() {

    if (application.isAuthenticated() === false) {
      return;
    }

    this.userId = application.getUser()._id;

  }

  unregisterEventListeners() {

    application.eventSystem.internal.offAllOn(this);

    this.cancelMinuteEventRegistration();

  }

  cancelMinuteEventRegistration() {

    if (this.minuteEventRegistration === null) {
      return;
    }

    this.minuteEventRegistration.cancel();

  }

  registerEventListeners(pollId) {

    function handleUserLoggedOutEvent() {

      this.userId = null;

      this.rerender();

    }

    application.eventSystem.internal.eventFor(this, internalEventCodes.USER_LOGGED_OUT, handleUserLoggedOutEvent.bind(this));

    function handlePollChoiceAddedEvent(data) {

      this.poll.choices.push(data.choice);

      filterEmptyPollChoices(this.poll);

      this.rerender();

    }

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.POST_POLL_CHOICE_ADDED}:${pollId}`, handlePollChoiceAddedEvent.bind(this));

    function handlePollChoiceUpdatedEvent(data) {

      const choice = data.choice;
      const choiceId = choice._id;
      const poll = this.poll;

      const choiceIndex = findIndexByProperty(poll.choices, '_id', choiceId);

      if (choiceIndex === -1) {
        poll.choices.push(choice);
      } else {
        poll.choices[choiceIndex] = choice;
      }

      filterEmptyPollChoices(this.poll);

      this.rerender();

    }

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.POST_POLL_CHOICE_UPDATED}:${pollId}`, handlePollChoiceUpdatedEvent.bind(this));

    function handlePollChoiceDeletedEvent(data) {

      const choice = data.choice;
      const choiceId = choice._id;
      const poll = this.poll;

      const choiceIndex = findIndexByProperty(poll.choices, '_id', choiceId);

      poll.choices.splice(choiceIndex, 1);

      filterEmptyPollChoices(this.poll);

      this.rerender();

    }

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.POST_POLL_CHOICE_DELETED}:${pollId}`, handlePollChoiceDeletedEvent.bind(this));

    function handlePollChoiceVoteAddedEvent(data) {

      this.poll = data.poll;

      filterEmptyPollChoices(this.poll);

      this.rerender();

    }

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.POST_POLL_CHOICE_VOTE_ADDED}:${pollId}`, handlePollChoiceVoteAddedEvent.bind(this));

    function handlePollChoiceVoteDeletedEvent(data) {

      this.poll = data.poll;

      filterEmptyPollChoices(this.poll);

      this.rerender();

    }

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.POST_POLL_CHOICE_VOTE_DELETED}:${pollId}`, handlePollChoiceVoteDeletedEvent.bind(this));

    function handlePollUpdatedEvent(data) {

      this.poll = {
        ...this.poll,
        ...data.poll
      };

      filterEmptyPollChoices(this.poll);

      this.rerender();

    }

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.POST_POLL_UPDATED}:${pollId}`, handlePollUpdatedEvent.bind(this));

    this.minuteEventRegistration = application.clock.onNewMinute(this.tryMakeTimeBasedUpdates.bind(this));

  }

  tryMakeTimeBasedUpdates() {

    this.updateExpiryLabelText();

    if (this.poll.endsAt <= Date.now()) {
      this.rerender();
    }

  }

  tryAttachEndedPollView(userId, poll) {

    if (canDisplayEndedPollView(userId, poll) === false) {
      return;
    }

    const shadowRoot = this.shadowRoot;

    const choicesList = list('ul.ended-poll-choices-list', EndedPollChoice);

    choicesList.update(poll.choices, { poll });

    this.endedPollViewEl = el('div.poll-ended-view-container', [choicesList]);

    mount(shadowRoot, this.endedPollViewEl);

  }

  tryAttachActivePollView(userId, poll) {

    if (canDisplayActivePollView(userId, poll) === false) {
      return;
    }

    if (canDisplayChoicesWithoutStats(userId, poll) === true) {
      this.renderActiveChoices(userId, poll, { stats: false });
      return;
    }

    this.renderActiveChoices(userId, poll, { stats: true });

  }

  getStyleEl() {

    const styleCode = `

      ul,
      ol {
        padding: 0;
      }
      li {
        margin: 0;
        padding: 0;
        list-style: none;
        margin-bottom: 20px;
        height: 53px;
        box-sizing: border-box;
      }
      li:last-child {
        margin-bottom: 0px;
      }

      button {
        cursor: pointer;
        font-size: 16px;
        line-height: 19px;
      }

      .poll-choice-list-item {
        position: relative;
        border-radius: var(--border-radius);
        overflow: hidden;
      }

      .poll-choice-percentage-bar {
        position: absolute;
        top: 0;
        left: 0;
      }

      .poll-choice-button {
        border-radius: var(--border-radius);
        border: 2px solid var(--border-color);
        width: 100%;
        cursor: pointer;
        background-color: var(--primary-background-color);
        font-weight: 700;
        color: var(--primary-color);
        text-align: center;
        padding: 15px 0;
      }
      .poll-choice-button:hover {
        background-color: var(--secondary-color);
      }

      .voted-view-poll-choice-button {
        background-color: var(--primary-background-color);
        display: grid;
        grid-template-columns: 20px auto 40px;
        column-gap: 8px;
        border-radius: var(--border-radius);
        border: 1px solid var(--border-color);
        width: 100%;
        padding: 15px 25px 15px 10px;
        color: var(--primary-color);
        align-items: center;
        text-align: left;
        font-weight: 400;
      }
      .voted-view-poll-choice-button:hover {
        background-color: var(--secondary-color);
      }

      .poll-choice-voted-icon {
        width: 21px;
        height: 21px;
      }

      .poll-choice-voted-icon.voted {
        background-image: var(--bg-image);
      }

      .poll-ended-view-stats-container {
        font-weight: 400;
        font-size: 12px;
        width: 70%;
        margin: 0 auto;
        color: var(--tertiary-color);

      }

      .ended-poll-choices-list li {
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius);
        font-weight: 400;
        font-size: 16px;
        line-height: 19px;
        padding: 16px 26px;
        position: relative;
        overflow: hidden;
        color: var(--primary-color);
      }

      .poll-choice-details-container {
        display: grid;
        grid-template-columns: 20px auto 40px;
        column-gap: 8px;
        position: relative;
        border-radius: var(--border-radius);
      }

      .poll-choice-details-container.two-column {
        grid-template-columns: auto fit-content(10px);
      }

      button.poll-choice-details-container {
        background-color: transparent;
        border: 1px solid var(--border-color);
        width: 100%;
        padding: 15px 25px 15px 10px;
        color: var(--primary-color);
        align-items: center;
        text-align: left;
        font-weight: 400;
      }

      button.poll-choice-details-container:hover {
        background-color: var(--secondary-color);
      }

      .poll-choice-percentage-bar {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        background-color: var(--startup-tertiary-color);
      }

      .ended-poll-choice-item {
        cursor: pointer;
      }

      .poll-choices-container, .poll-ended-view-container {
        width: 70%;
        margin: 0 auto;
      }


    `;

    const style = el('style', styleCode);

    return style;

  }

  renderPollStats() {

    const poll = this.poll;

    if (poll.choices.length === 0) {
      return;
    }

    const votesCount = el('span.poll-ended-view-total-votes-count', {}, `Total ${poll.totalVotesCount} votes.   `);
    this.statsContainer = el('div.poll-ended-view-stats-container', [votesCount, this.expiryLabel]);

    this.updateExpiryLabelText();

    mount(this.shadowRoot, this.statsContainer);

  }

  updateExpiryLabelText() {

    const poll = this.poll;
    const endsAt = poll.endsAt;
    let expiryLabelText = '';
    let formattedEndsAt = '';

    if (endsAt !== null) {
      if (endsAt <= Date.now()) {
        expiryLabelText = 'The poll is ended.';
      } else {
        expiryLabelText = this.computeExpiryLabelText(poll);
      }
      formattedEndsAt = formatToLongDate(endsAt);
    }

    if (this.expiryLabel.textContent !== expiryLabelText) {

      this.expiryLabel.textContent = expiryLabelText;
      this.expiryLabel.setAttribute('title', formattedEndsAt);

    }

  }

  renderActiveChoices(userId, poll, options) {

    let PollChoiceClass = PollChoice;

    if (options.stats === true) {
      PollChoiceClass = PollChoiceWithStats;
    }

    this.choicesEl = list('ul.poll-choice-buttons', PollChoiceClass);

    this.choicesEl.update(poll.choices, { poll });

    this.activePollViewEl = el('div.poll-choices-container', this.choicesEl);

    mount(this.shadowRoot, this.activePollViewEl);

  }

  computeExpiryLabelText(poll) {

    let labelText = '';

    const difference = poll.endsAt - Date.now();

    const days = difference / (1000 * 60 * 60 * 24);
    const flooredDays = Math.floor(days);

    if (days >= 1) {
      labelText += `${flooredDays} days`;
    }

    let hours = difference / (1000 * 60 * 60);

    if (hours >= 24) {
      hours = (days % flooredDays) * 24;
    }

    const flooredHours = Math.floor(hours);
    if (flooredHours > 0) {
      labelText = `${labelText} ${flooredHours} hours`.trim();
    }

    let minutes = difference / (1000 * 60);

    if (minutes >= 60) {
      minutes = (hours % flooredHours) * 60;
    }

    const flooredMinutes = Math.floor(minutes);
    if (minutes > 0) {
      labelText = `${labelText} ${flooredMinutes} minutes`.trim();
    }

    return `${labelText} left`;

  }

  renderSpinner() {

    this.spinner = el('ht-spinner');

    mount(this.shadowRoot, this.spinner);

  }

  removeSpinner() {
    unmount(this.shadowRoot, this.spinner);
  }

  renderSomethingWentWrong() {

    mount(this.shadowRoot, el('div.something-went-wrong', {}, 'Not able to load poll!'));

  }

  unmountAll() {

    if (this.endedPollViewEl) {
      unmount(this.shadowRoot, this.endedPollViewEl);
    }

    if (this.activePollViewEl) {
      unmount(this.shadowRoot, this.activePollViewEl);
    }

    if (this.activeVotedPollViewEl) {
      unmount(this.shadowRoot, this.activeVotedPollViewEl);
    }

    if (this.statsContainer) {
      unmount(this.shadowRoot, this.statsContainer);
    }

  }

  rerender() {

    this.unmountAll();
    this.tryRenderAll();

  }

  tryRenderAll() {

    const userId = this.userId;
    const poll = this.poll;

    this.removeSpinner();
    this.tryAttachEndedPollView(userId, poll);
    this.tryAttachActivePollView(userId, poll);
    this.renderPollStats();

  }

  static removeExistingVote(choices, voterId) {

    for (let choice of choices) {

      const voterIds = choice.voterIds;
      const voteIndex = voterIds.indexOf(voterId);

      if (voteIndex === -1) {
        continue;
      }

      voterIds.splice(voteIndex, 1);
      choice.votesCount--;

    }

  }

}

export default PollingBox;